import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import RateVisit from "@/pages/RateVisit";
import QuoteDecision from "@/pages/QuoteDecision";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import Agents from "@/pages/admin/Agents";
import MapPage from "@/pages/admin/MapPage";
import Admins from "@/pages/admin/Admins";
import BranchReport from "@/pages/admin/BranchReport";
import Prices from "@/pages/admin/Prices";
import InspectionDetail from "@/pages/admin/InspectionDetail";
import AgentTasks from "@/pages/agent/AgentTasks";
import InspectionWizard from "@/pages/agent/InspectionWizard";
import DailyRoute from "@/pages/agent/DailyRoute";
import { Loader2 } from "lucide-react";

const Guard = ({ role, children }) => {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen grid place-items-center" data-testid="auth-loading">
        <Loader2 className="animate-spin text-brand-red" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/agent"} replace />;
  return children;
};

const PermRoute = ({ perm, owner, children }) => {
  const { user, can } = useAuth();
  return (owner ? user.is_owner : can(perm)) ? children : <Navigate to="/admin" replace />;
};

const Home = () => {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/agent"} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/r/:token" element={<RateVisit />} />
          <Route path="/q/:token" element={<QuoteDecision />} />
          <Route path="/admin" element={<Guard role="admin"><AdminLayout /></Guard>}>
            <Route index element={<Dashboard />} />
            <Route path="agents" element={<PermRoute perm="agents"><Agents /></PermRoute>} />
            <Route path="map" element={<PermRoute perm="view"><MapPage /></PermRoute>} />
            <Route path="admins" element={<PermRoute owner><Admins /></PermRoute>} />
            <Route path="reports" element={<PermRoute perm="view"><BranchReport /></PermRoute>} />
            <Route path="prices" element={<PermRoute perm="settings"><Prices /></PermRoute>} />
            <Route path="inspections/:id" element={<InspectionDetail />} />
          </Route>
          <Route path="/agent" element={<Guard role="agent"><AgentTasks /></Guard>} />
          <Route path="/agent/route" element={<Guard role="agent"><DailyRoute /></Guard>} />
          <Route path="/agent/inspections/:id" element={<Guard role="agent"><InspectionWizard /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors dir="rtl" toastOptions={{ style: { fontFamily: "Cairo, sans-serif" } }} />
    </AuthProvider>
  );
}

export default App;
